import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import CountUp from "react-countup";
import Scale from "./scale";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./style/theme";
import Features from "./Features";
import Steps from "./Steps";
import Footer from "./Footer";
import { styles } from "./style/main";

const StatCard = ({
  icon,
  title,
  value,
  hover,
  onMouseEnter,
  onMouseLeave,
  prefix,
  suffix,
}) => (
  <Grid
    item
    xs={12}
    sm={6}
    md={3}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    sx={{
      p: 3,
      borderRadius: 2,
      boxShadow: 3,
      transition: "transform 0.3s ease-in-out",
      "&:hover": {
        transform: "scale(1.05)",
      },
      borderLeft: "4px solid",
      borderColor: "primary.main",
      bgcolor: "background.paper",
      justifyContent: "center",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
      {icon}
      <Typography variant="h7" sx={{ ml: 2, fontWeight: "bold" }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
      {hover ? (
        <CountUp
          end={value}
          start={0}
          duration={1.3}
          separator=","
          prefix={prefix}
          suffix={suffix}
        />
      ) : (
        `${prefix} ${value.toLocaleString()} ${suffix}`
      )}
    </Typography>
  </Grid>
);

export default function Main() {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play();
        } else {
          videoRef.current?.pause();
        }
      },
      { threshold: 0.5 },
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ position: "relative", overflowY: "hidden" }}>
        <Box
          sx={{
            background:
              "linear-gradient(to bottom, #FFFFFF 2%, #F0F0FF 60%, #eeeeffff 10%, #FFFFFF 100%)",
            py: 10,
          }}
        >
          <Container maxWidth="xl">
            <Typography
              variant="h2"
              align="center"
              sx={{ mb: 2, color: "primary.main" }}
            >
              AI That Knows When to Scale
              <Typography
                variant="h2"
                component="span"
                sx={{ color: "secondary.main" }}
              >
                {" "}
                and <br /> When to Save
              </Typography>
            </Typography>
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{ mb: 4, letterSpacing: "1.2px" }}
            >
              We predict and manage your AWS instances so you only pay for what
              you truly need,
              <br />
              With setup to savings in under 30 minutes.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 8 }}>
              <Button
                href="get-started"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardOutlinedIcon />}
              >
                Book a Demo
              </Button>
            </Box>
            <Grid
              container
              spacing={4}
              justifyContent="center"
              direction="row"
              alignItems="center"
            >
              <StatCard
                icon={
                  <TrendingUpOutlinedIcon
                    color="primary"
                    sx={{ fontSize: 40 }}
                  />
                }
                title="Cumulative Savings"
                value={1326}
                hover={hovered === 0}
                onMouseEnter={() => setHovered(0)}
                onMouseLeave={() => setHovered(null)}
                prefix="$ "
                suffix="USD"
              />
              <StatCard
                icon={
                  <LeaderboardOutlinedIcon
                    color="primary"
                    sx={{ fontSize: 40 }}
                  />
                }
                title="Current Monthly Spend"
                value={2514}
                hover={hovered === 1}
                onMouseEnter={() => setHovered(1)}
                onMouseLeave={() => setHovered(null)}
                prefix="$ "
                suffix="USD"
              />
              <StatCard
                icon={<AttachMoneyIcon color="primary" sx={{ fontSize: 40 }} />}
                title="Average Annual Savings"
                value={24000}
                hover={hovered === 2}
                onMouseEnter={() => setHovered(2)}
                onMouseLeave={() => setHovered(null)}
                prefix="$ "
                suffix="USD"
              />
              <StatCard
                icon={
                  <TrendingDownOutlinedIcon
                    color="primary"
                    sx={{ fontSize: 40 }}
                  />
                }
                title="Average Cost Reduction"
                value={50}
                hover={hovered === 3}
                onMouseEnter={() => setHovered(3)}
                onMouseLeave={() => setHovered(null)}
                prefix=""
                suffix="%"
              />
            </Grid>
          </Container>
        </Box>

        <Scale />

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            // width:"80vw",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
            justifyItems: "center",
            width: { lg: "100%", xs: "100%" },
            height: { lg: "80vh", xs: "100%" },
            pb: 2,
            pt: 5,
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              height: { lg: "100%", xs: "100%" },
              backgroundColor: "#f9f9ff",
              p: { lg: 1, md: 3, xs: 1 },
              // width: { lg: "80%", md: "80%", xs: "99vw" },
            }}
          >
            <Box
              component="img"
              src="/images/auto-scaling.png"
              alt="Auto-scaling"
              sx={{
                position: "absolute",
                top: { lg: 120, xs: 200, md: 200 },
                right: { lg: 220, md: 260, sm: 220, xs: -50 },
                width: { xs: "40%", md: "30%", sm: "40%", lg: "30%" },
                maxWidth: 550,
                zIndex: 1,
                opacity: 0.9,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                maxWidth: "1000px",
                mx: "auto",
                px: 2,
              }}
            >
              <Grid
                item
                sx={{
                  mt: { lg: 10, xs: 3 },
                  display: "flex",
                }}
              >
                <Grid sx={styles.silent_drain}>
                  <IconButton
                    sx={{ alignContent: "center", color: "rgb(238, 62, 59)" }}
                  >
                    <ReportProblemOutlinedIcon fontSize="small" />
                  </IconButton>
                  The Silent Cloud Drain
                </Grid>
              </Grid>
              <Typography
                variant="h4"
                align="left"
                sx={{ mb: 2, mt: 2, color: "primary.main" }}
              >
                Most companies waste 30-60% <br /> of their cloud budget
              </Typography>

              <Grid
                item
                sx={{
                  fontSize: { lg: "1em", md: "0.8em", xs: "1em" },
                  color: "rgb(98, 97, 97)",
                  textAlign: "left",
                  ml: { lg: 0, xs: 1 },
                  fontWeight: 500,
                }}
              >
                on unused or poorly optimized resources. Are you unknowingly{" "}
                <br></br> overspending every month?
              </Grid>
              <Grid
                container
                direction="row"
                mt={4}
                sx={{ gap: { lg: 2, md: 3, sm: 2, xs: 2 } }}
              >
                <Grid item lg={6} xs={12} md={5}>
                  <Grid container sx={styles.budget_column}>
                    <Grid item sx={styles.budget_awsmo}>
                      Without Awsmo
                    </Grid>
                    <Grid
                      item
                      sx={{
                        ...styles.budget_head,
                      }}
                    >
                      Wasted Budget
                    </Grid>
                    <Grid item sx={styles.budget_para}>
                      {/* Awsmo eliminates idle compute, migrates unused SQL
                      volumes, and right-sizes EC2 instances to cut costs. */}
                      Money vanishes on idle servers, unused databases, and
                      oversized resources.
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item lg={6} xs={12} md={5}>
                  <Grid container sx={styles.budget_column}>
                    <Grid
                      item
                      sx={{ ...styles.budget_awsmo, color: "#71ba85" }}
                    >
                      With Awsmo
                    </Grid>
                    <Grid
                      item
                      sx={{
                        ...styles.budget_head,
                      }}
                    >
                      Optimized Costs
                    </Grid>
                    <Grid item sx={styles.budget_para}>
                      {/* Smart AI optimization that maintains performance while
                      reducing unnecessary cloud spending */}
                      AI-powered automation ensures you pay only for what you
                      need, cutting waste while keeping performance sharp.
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>

        <Features />

        <Box sx={{ py: 10 }}>
          <Container maxWidth="lg">
            <Grid container spacing={8} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" sx={{ mb: 2, color: "primary.main" }}>
                  Full Transparency Across Your AWS Usage
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Gain full visibility into every dollar spent and every action
                  taken across your AWS infrastructure. Easier to use than
                  native AWS tools, with real-time insights and transparent
                  scaling decisions that put you in control.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} display="flex" justifyContent="center">
                <video
                  ref={videoRef}
                  width="90%"
                  height="90%"
                  muted
                  autoPlay
                  loop
                  preload="auto"
                  playsInline
                >
                  <source src="/videos/savings.mp4" type="video/mp4" />
                </video>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Steps />
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
